import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid4 } from 'uuid';
import camelize from 'camelize';
import throttle from 'lodash/throttle';
import { useMediaDataCache, useCachedMedia, withMediaDataCache } from 'context';
import StyledScrollbar from 'components/common/StyledScrollbar';
import ErrorBoundary from 'components/common/ErrorBoundary';
import Icon from 'components/common/Icon';
import { ManagementForm } from 'components/common/inlines';
import InlineListBuilder, { Controls as ListBuilderControls } from 'components/common/inlines/InlineListBuilder';
import { ObjectListModal } from 'components/views/ObjectList';
import ItemSettingsForm from './ItemSettingsForm';
import PlaylistItem from './PlaylistItem';

const PlaylistBuilder = ({ formsetData, parentId, hasError }) => {
  const [itemsById, setItemsById] = useState({});
  const [itemsOrdered, setItemsOrdered] = useState([]);
  const [editMode, setEditMode] = useState((formsetData.forms.length === 0 || hasError) ? 'insert' : 'sort');
  const [mediaModalCallback, setMediaModalCallback] = useState(() => null);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState(null);

  const itemsArray = useMemo(() => (
    itemsOrdered.map(id => itemsById[id])
  ), [JSON.stringify(itemsOrdered), JSON.stringify(itemsById)]);

  const updateItem = useCallback((id, value) => setItemsById(os => {
    const origItem = os[id];
    const newItem = typeof value === 'function' ? value(origItem) : value;
    return { ...os, [id]: newItem };
  }), [setItemsById]);

  const nextItemIndex = useRef(0);

  const mediaIds = formsetData.forms.map(f => f.fields.media.value);
  useMediaDataCache(mediaIds);
  const activeSettingsItem = useCachedMedia(activeItemId && itemsById[activeItemId].fields.media);

  useEffect(() => {
    initItems();
  }, []);

  const handleEditModeChange = editMode => {
    setEditMode(editMode);
    if (editMode === 'sort') {
      setActiveItemId(null);
    }
  };

  const nodeTypes = [
    {
      name: 'Media',
      callback: idx => {
        const cb = items => {
          const newItems = items.map(item => initNewItem('media', item.uuid));
          const newIndex = getPaddedIndex(idx);
          insertItems(newItems, newIndex);
          handleMediaModalRequestClose();
        };
        setMediaModalCallback(() => cb);
        setMediaModalOpen(true);
      },
    },
    {
      name: 'Group',
      callback: idx => {
        const newItem = initNewItem('group');
        const newIndex = getPaddedIndex(idx);
        insertItems([newItem], newIndex);
      },
    },
    {
      name: 'Label',
      callback: idx => {
        const newItem = initNewItem('label');
        const newIndex = getPaddedIndex(idx);
        insertItems([newItem], newIndex);
      },
    },
  ];

  const handleInsertGroupMediaClick = (groupUid, idx) => {
    const cb = items => {
      const globalIndex = itemsOrdered.indexOf(groupUid) + 1 + idx;
      const newItems = items.map(item => initNewItem('media', item.uuid, groupUid));
      insertItems(newItems, globalIndex);
      handleMediaModalRequestClose();
    };
    setMediaModalCallback(() => cb);
    setMediaModalOpen(true);
  };

  const handleItemSettingsClick = itemId => setActiveItemId(os => os === itemId ? null : itemId);

  const handleSettingsFormChange = fields => {
    updateItem(activeItemId, orig => ({
      ...orig,
      fields: {
        ...orig.fields,
        ...fields,
      },
    }));
  };

  const handleMediaModalRequestClose = () => setMediaModalOpen(false);

  const handleItemMove = useCallback(throttle((sourceId, targetId, insertPos) => {
    const sourceItem = itemsById[sourceId];
    const targetItem = itemsById[targetId];

    if (targetItem && targetItem.fields.groupUid && sourceItem.fields.type !== 'media') {
      // Only media can be nested into a group.
      return;
    }

    let newItemsOrdered;
    let toIndex;
    let targetGroupId = targetItem ? targetItem.fields.groupUid : null;
    if (sourceItem.fields.type === 'group') {
      // We are moving a group, so need to reindex all child items.
      const subItemIds = itemsOrdered.filter(id => itemsById[id].fields.groupUid === sourceId);
      newItemsOrdered = itemsOrdered.filter(id => id !== sourceId && !subItemIds.includes(id));
      toIndex = insertPos === 'before' ? newItemsOrdered.indexOf(targetId) : newItemsOrdered.indexOf(targetId) + 1;
      newItemsOrdered.splice(toIndex, 0, sourceId, ...subItemIds);
    } else if (targetId.indexOf('placeholder') > -1) {
      // We are moving an item into an empty group.
      if (sourceItem.fields.type !== 'media') {
        return;
      }
      // TODO - something better here
      targetGroupId = targetId.match(/group-(.+)-placeholder/)[1];
      newItemsOrdered = itemsOrdered.filter(item => item !== sourceId);
      toIndex = itemsOrdered.indexOf(targetGroupId) + 1;
      newItemsOrdered.splice(toIndex, 0, sourceId);
    } else {
      newItemsOrdered = itemsOrdered.filter(item => item !== sourceId);
      toIndex = insertPos === 'before' ? newItemsOrdered.indexOf(targetId) : newItemsOrdered.indexOf(targetId) + 1;
      newItemsOrdered.splice(toIndex, 0, sourceId);
    }

    setItemsOrdered(newItemsOrdered);
    updateItem(sourceId, orig => ({
      ...orig,
      fields: {
        ...orig.fields,
        groupUid: targetGroupId,
      },
    }));
  }, 100), [
    JSON.stringify(itemsById),
    JSON.stringify(itemsOrdered),
  ]);

  const getPaddedIndex = idx => {
    // For a given top-level index, return a padded index that includes
    // all preceding items contained in groups.
    if (idx === 0) {
      return idx;
    }

    const nextItem = itemsArray.filter(item => !item.fields.groupUid)[idx];
    if (nextItem) {
      return itemsOrdered.indexOf(nextItem.itemId);
    } else {
      return itemsOrdered.length;
    }
  };

  const initNewItem = (type, mediaId = null, group = null) => {
    const itemId = uuid4();
    const item = {
      itemId,
      prefix: `${formsetData.prefix}-${nextItemIndex.current}`,
      errors: {},
      fields: {
        id: null,
        index: null,
        type,
        groupUid: group,
        media: mediaId,
        name: '',
        slug: '',
        description: '',
        parent: parentId,
        uuid: itemId,
        posterDuration: 0,
      },
    };

    nextItemIndex.current += 1;
    return item;
  };

  const initItems = () => {
    // Hydrate items list from initial formset data
    const { forms } = camelize(formsetData);

    const sortedForms = [...forms].sort((a, b) => (
      parseInt(a.fields.index.value, 10) - parseInt(b.fields.index.value, 10)
    ));

    const byId = {};
    const ordered = [];
    let shouldDelete = false;
    sortedForms.forEach(form => {
      const itemId = (form.isBound ? form.fields.uuid.value : form.fields.uuid.initial) || uuid4();
      const fields = Object.keys(form.fields).reduce((result, key) => {
        if (key === 'DELETE') {
          shouldDelete = !!form.fields[key].value;
        } else {
          result[key] = form.fields[key].value;
        }
        return result;
      }, {});
      fields.index = nextItemIndex.current;

      const item = {
        itemId,
        fields,
        shouldDelete,
        prefix: form.prefix,
        errors: form.errors,
      };

      byId[itemId] = item;
      ordered.push(itemId);
      nextItemIndex.current += 1;
    });
    setItemsById(byId);
    setItemsOrdered(ordered);
  };

  const insertItems = useCallback((items, idx) => {
    const newItems = items.reduce((result, item) => {
      result[item.itemId] = item;
      return result;
    }, {});
    const newItemIds = items.map(({ itemId }) => itemId);
    setItemsById(os => ({ ...os, ...newItems }));
    setItemsOrdered(os => [...os.slice(0, idx), ...newItemIds, ...os.slice(idx)]);
  }, [setItemsById, setItemsOrdered]);

  const renderedItems = itemsArray.filter(item => !item.fields.groupUid).map(({ itemId, ...rest }) => {
    const subItems = itemsArray.filter(item => item.fields.groupUid === itemId).map(item => ({ ...item, itemIndex: itemsOrdered.indexOf(item.itemId) }));
    const itemIndex = itemsOrdered.indexOf(itemId);
    const subItemCallback = idx => handleInsertGroupMediaClick(itemId, idx);
    return (
      <PlaylistItem
        key={itemId}
        itemId={itemId}
        itemIndex={itemIndex}
        parentId={parentId}
        editMode={editMode}
        subItems={subItems}
        activeItemId={activeItemId}
        settingsActive={activeItemId === itemId}
        onSettingsClick={handleItemSettingsClick}
        onSubItemMove={handleItemMove}
        onLoadPreview={() => null}
        onAddMedia={subItemCallback}
        {...rest}
      />
    );
  });

  const { prefix, managementForm } = camelize(formsetData);
  const showSettingsForm = !!activeItemId;
  let settingsFormTitle;
  if (showSettingsForm) {
    settingsFormTitle = (activeSettingsItem || {}).name;
  }
  const formCount = itemsOrdered.length;
  const ungroupedItemIds = itemsOrdered.filter(itemId => !itemsById[itemId].fields.groupUid);

  return (
    <div className="title-builder player-builder object-detail-panel-full-height">
      <ErrorBoundary>
        <ManagementForm prefix={prefix} formData={managementForm} totalForms={formCount} />
        <div style={{ flex: 1, height: '100%' }}>
          <StyledScrollbar>
            <div style={{ padding: 30 }}>
              <ListBuilderControls mode={editMode} empty={itemsOrdered.length === 0} onChangeMode={handleEditModeChange} />
              <InlineListBuilder
                editMode={editMode}
                nodeTypes={nodeTypes}
                itemIds={ungroupedItemIds}
                onItemMove={handleItemMove}
              >
                {renderedItems}
              </InlineListBuilder>

              {itemsOrdered.length === 0 ? (
                <div className="empty my-4">
                  <div className="empty-icon">
                    <Icon name="list" size={60} />
                  </div>
                  <p className="empty-title h3">Empty Playlist</p>
                  <p className="empty-subtitle">Add items to get started.</p>
                </div>
              ) : null}

              <ObjectListModal
                model="media"
                preFilter={{ type__in: 'audio,video' }}
                disabledFilters={['type', 'width', 'height']}
                isOpen={mediaModalOpen}
                onAccept={mediaModalCallback}
                onRequestClose={handleMediaModalRequestClose}
              />
            </div>
          </StyledScrollbar>
        </div>
        <div style={{ flex: '0 0 509px' }}>
          {showSettingsForm && (
            <ItemSettingsForm
              formTitle={settingsFormTitle}
              initialValues={{
                slug: itemsById[activeItemId].fields.slug,
                posterDuration: itemsById[activeItemId].fields.posterDuration,
              }}
              onChange={handleSettingsFormChange}
            />
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

PlaylistBuilder.propTypes = {
  formsetData: PropTypes.object,
  parentId: PropTypes.string,
  hasError: PropTypes.bool,
};

export default withMediaDataCache(PlaylistBuilder);
